import React from 'react'

import * as Sentry from '@sentry/react'

import { isRouteErrorResponse, useRouteError } from 'react-router-dom'

import { ResponseError } from '@/lib/api/lib'
import { FetchError } from '@/lib/fetchJson'

import { AppContext } from '@/components/base/appcontext'
import BackButton from '@/components/base/backButton'
import AnimatedWrapper from '@/components/base/animatedWrapper'

import { Typography } from '@/components/ui/typography'

function ErrorPage() {
    const appContext = React.useContext(AppContext)
    const error = useRouteError()
    const [errorMessage, setErrorMessage] = React.useState('')
    const [stackTrace, setStackTrace] = React.useState<string | null>(null)

    React.useEffect(() => {
        if (error instanceof FetchError) {
            Sentry.withScope((scope) => {
                scope.setTag('fetch_error', 'true')
                scope.setExtra('data', error.data)
                scope.setExtra('message', error.message)
                scope.setExtra('name', error.name)
                scope.setExtra('request', error.request)
                scope.setExtra('response', error.response)
                Sentry.captureException(error)
            })
        } else {
            Sentry.captureException(error)
        }

        async function extractErrorMessage() {
            if (isRouteErrorResponse(error)) {
                setErrorMessage(error.data?.message || error.statusText)
                if (error.data?.stack) {
                    setStackTrace(error.data.stack)
                }
            } else if (error instanceof ResponseError) {
                if (error.response.status >= 400) {
                    try {
                        setErrorMessage(
                            JSON.stringify(await error.response.json())
                        )
                    } catch {
                        setErrorMessage(JSON.stringify(error.response.body))
                    }
                }
            } else if (error instanceof Error) {
                setErrorMessage(error.message)
                setStackTrace(error.stack || null)
            } else if (typeof error === 'string') {
                setErrorMessage(error)
                setStackTrace(null)
            } else {
                console.error(error)
                setErrorMessage('Unknown error')
                setStackTrace(null)
            }
        }
        extractErrorMessage()
    }, [error])

    return (
        <AnimatedWrapper animationName="fadeIn" duration={500}>
            <div className="flex justify-center flex-col w-screen h-screen">
                <div className="flex flex-col gap-6 m-10">
                    <Typography variant="title-lg">
                        {appContext.geti18n('base.error.title.text')}
                    </Typography>
                    <Typography variant="body-md">
                        {appContext.geti18n('base.error.subtitle.text')}
                    </Typography>
                    <Typography
                        variant="body-sm"
                        className="whitespace-pre-wrap"
                    >
                        {errorMessage}
                    </Typography>
                    {import.meta.env.VITE_SKIP_STACK === 'false' &&
                        stackTrace && (
                            <Typography
                                variant="body-sm"
                                component="pre"
                                className="whitespace-pre-wrap bg-muted p-4 rounded"
                            >
                                {stackTrace}
                            </Typography>
                        )}
                    <BackButton />
                </div>
            </div>
        </AnimatedWrapper>
    )
}

export default ErrorPage
